import {
  nextTick, onBeforeUnmount, onMounted, ref, type Ref,
} from 'vue';

interface CardScrollerApi {
  trackRef: Ref<HTMLElement | null>;
  canScrollBack: Ref<boolean>;
  canScrollForward: Ref<boolean>;
  hasOverflow: Ref<boolean>;
  scrollBack: () => void;
  scrollForward: () => void;
  syncScrollState: () => void;
}

/**
* Horizontal scrolling for a scroll-snap track.
*
* The track scrolls natively — swipe on touch, arrow keys when focused. This
* only adds the desktop arrows and keeps their disabled state in sync, so
* everything here is progressive enhancement over something that already
* works without it.
*/
const useCardScroller = (): CardScrollerApi => {
  const trackRef = ref<HTMLElement | null>(null);
  const canScrollBack = ref(false);
  const canScrollForward = ref(false);
  const hasOverflow = ref(false);

  // Sub-pixel rounding means scrollLeft rarely lands exactly on the edge.
  const EDGE_TOLERANCE = 4;

  const syncScrollState = () => {
    const track = trackRef.value;
    if (!track) return;

    const maxScroll = track.scrollWidth - track.clientWidth;
    hasOverflow.value = maxScroll > EDGE_TOLERANCE;
    canScrollBack.value = track.scrollLeft > EDGE_TOLERANCE;
    canScrollForward.value = track.scrollLeft < maxScroll - EDGE_TOLERANCE;
  };

  /**
   * Measuring on mount is too early: the nodes are in the DOM but the browser
   * has not laid them out yet, so scrollWidth still equals clientWidth and the
   * arrows stay hidden until the first manual scroll. Waiting for the next
   * paint gives us real numbers.
   */
  const syncAfterPaint = async () => {
    await nextTick();
    if (typeof globalThis.requestAnimationFrame === 'function') {
      globalThis.requestAnimationFrame(syncScrollState);
      return;
    }
    syncScrollState();
  };

  /**
   * Scrolls by one card rather than a fixed amount, so the step stays right
   * as the card width changes across breakpoints.
   */
  const stepSize = (track: HTMLElement): number => {
    const firstCard = track.firstElementChild as HTMLElement | null;
    return firstCard?.offsetWidth ?? track.clientWidth;
  };

  const scrollByStep = (direction: 1 | -1) => {
    const track = trackRef.value;
    if (!track) return;
    track.scrollBy({ left: stepSize(track) * direction, behavior: 'smooth' });
  };

  const scrollBack = () => scrollByStep(-1);
  const scrollForward = () => scrollByStep(1);

  let resizeObserver: ResizeObserver | null = null;
  let mutationObserver: MutationObserver | null = null;

  onMounted(() => {
    // Guarded for SSR — this composable is only ever exercised in the browser,
    // but the module itself is evaluated on the server.
    if (typeof globalThis.window === 'undefined') return;

    syncAfterPaint();

    const track = trackRef.value;
    if (!track) return;

    if ('ResizeObserver' in globalThis.window) {
      resizeObserver = new ResizeObserver(syncScrollState);
      resizeObserver.observe(track);
      // The track itself keeps its size when cards come and go, so observing
      // only the container would miss the change that matters.
      Array.from(track.children).forEach((child) => resizeObserver?.observe(child));
    }

    if ('MutationObserver' in globalThis.window) {
      mutationObserver = new MutationObserver(() => {
        Array.from(track.children).forEach((child) => resizeObserver?.observe(child));
        syncAfterPaint();
      });
      mutationObserver.observe(track, { childList: true });
    }
  });

  onBeforeUnmount(() => {
    resizeObserver?.disconnect();
    resizeObserver = null;
    mutationObserver?.disconnect();
    mutationObserver = null;
  });

  return {
    trackRef,
    canScrollBack,
    canScrollForward,
    hasOverflow,
    scrollBack,
    scrollForward,
    syncScrollState,
  };
};

export default useCardScroller;
